export type FirmwareUpdateVariant =
  | 'v16'
  | 'v16-signed'
  | 'v201'
  | 'v21';

export type FirmwareUpdateValidationResult =
  | { valid: true; payload: Record<string, unknown> }
  | { valid: false; errors: string[] };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const isNumber = (value: unknown): value is number =>
  typeof value === 'number' && !Number.isNaN(value);

const requireField = (
  errors: string[],
  source: Record<string, unknown>,
  field: string,
  predicate: (value: unknown) => boolean,
  errorKey: string
) => {
  if (!predicate(source[field])) {
    errors.push(errorKey);
  }
};

const validateSignedFirmwarePayload = (
  payload: Record<string, unknown>,
  requireSignature: boolean
): string[] => {
  const errors: string[] = [];

  requireField(
    errors,
    payload,
    'requestId',
    isNumber,
    'firmwareUpdateMissingRequestId'
  );

  const firmwareValue = payload['firmware'];
  if (!isRecord(firmwareValue)) {
    errors.push('firmwareUpdateMissingFirmware');
    return errors;
  }

  requireField(
    errors,
    firmwareValue,
    'location',
    isNonEmptyString,
    'firmwareUpdateMissingLocation'
  );
  requireField(
    errors,
    firmwareValue,
    'retrieveDateTime',
    isNonEmptyString,
    'firmwareUpdateMissingRetrieveDateTime'
  );
  requireField(
    errors,
    firmwareValue,
    'signingCertificate',
    isNonEmptyString,
    'firmwareUpdateMissingSigningCertificate'
  );
  if (requireSignature) {
    requireField(
      errors,
      firmwareValue,
      'signature',
      isNonEmptyString,
      'firmwareUpdateMissingSignature'
    );
  }

  return errors;
};

const validateUnsignedV16Payload = (
  payload: Record<string, unknown>
): string[] => {
  const errors: string[] = [];

  requireField(
    errors,
    payload,
    'location',
    isNonEmptyString,
    'firmwareUpdateMissingLocation'
  );
  requireField(
    errors,
    payload,
    'retrieveDate',
    isNonEmptyString,
    'firmwareUpdateMissingRetrieveDate'
  );

  return errors;
};

export const getFirmwareUpdateVariant = (
  ocppProtocol: string,
  signed: boolean
): FirmwareUpdateVariant | undefined => {
  if (ocppProtocol === 'V_16') {
    return signed ? 'v16-signed' : 'v16';
  }
  if (ocppProtocol === 'V_201') {
    return 'v201';
  }
  if (ocppProtocol === 'V_21') {
    return 'v21';
  }
  return undefined;
};

export const validateFirmwareUpdatePayload = (
  rawPayload: string,
  variant: FirmwareUpdateVariant
): FirmwareUpdateValidationResult => {
  let parsed: unknown;

  try {
    parsed = JSON.parse(rawPayload);
  } catch {
    return { valid: false, errors: ['firmwareUpdateInvalidJson'] };
  }

  if (!isRecord(parsed)) {
    return { valid: false, errors: ['firmwareUpdateInvalidJson'] };
  }

  const errors =
    variant === 'v16'
      ? validateUnsignedV16Payload(parsed)
      : validateSignedFirmwarePayload(parsed, variant !== 'v21');

  if (errors.length > 0) {
    return { valid: false, errors };
  }

  return { valid: true, payload: parsed };
};
